/**
 * Creates a timer that accepts a `timerCalc` function to perform
 * calculated timeout retries, such as exponential backoff.
 *
 * @example
 * let reconnectTimer = new Timer(() => this.connect(), function(tries){
 *   return [1000, 5000, 10000][tries - 1] || 10000
 * })
 * reconnectTimer.scheduleTimeout() // fires after 1000
 * reconnectTimer.scheduleTimeout() // fires after 5000
 * reconnectTimer.reset()
 * reconnectTimer.scheduleTimeout() // fires after 1000
 *
 * @param callback The callback to trigger when the timeout elapses.
 * @param timerCalc The function to calculate the timeout.
 */
export class Timer {
  private timer: ReturnType<typeof setTimeout> | undefined
  private tries = 0

  /** @constructor */
  constructor(
    private readonly callback: () => void,
    private readonly timerCalc: (tries: number) => number,
    private readonly maxTries: number = 10,
  ) {}

  /** Resets the timer. */
  reset() {
    this.tries = 0
    clearTimeout(this.timer)
  }

  /**
   * Cancels any previous scheduleTimeout and schedules a new callback.
   */
  scheduleTimeout() {
    clearTimeout(this.timer)

    this.timer = setTimeout(
      () => {
        this.tries = this.tries + 1
        if (this.tries <= this.maxTries) {
          this.callback()
        }
      },
      this.timerCalc(this.tries + 1),
    )
  }
}
